import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Slider from 'src/components/shared/Slider'
import { useAnalytics } from '../../../../../common/analytics'
import { useRouter } from 'next/router'
import getConfig from 'src/common/runtimeConfig'
import { useLeadId } from '../../../../../common/lead-id'
import styles from './index.module.scss'
import { getCookies } from 'src/common/helpers'
import { useNavigateToApply } from 'src/common/hooks/useNavigateToApply'
import AnimatedArrowRedCTA from 'src/components/shared/CallToActionButton/AnimatedArrowRedCTA'
import { TrustPilot } from 'src/components/shared/TrustMarkerWidgets'
import classNames from 'classnames'
import { Section } from 'src/components/shared/SectionV2'
import Link from 'next/link'
import Tooltip from 'src/components/shared/Tooltip'

const DASHED_UNDERLINE =
  'pb-[2px] [background-image:linear-gradient(to_right,#737373_50%,transparent_50%)] [background-size:4px_1px] [background-position:0_100%] [background-repeat:repeat-x]'

const TOOLTIP_LINK_CLASS = 'text-white no-underline hover:underline'

const TOOLTIP_BODY = (
  <p className="m-0">
    <Link href="/debt-relief/" className={TOOLTIP_LINK_CLASS} tabIndex={-1}>
      Debt relief
    </Link>
    , also known as{' '}
    <Link
      href="/debt-solutions/debt-settlement/"
      className={TOOLTIP_LINK_CLASS}
      tabIndex={-1}
    >
      debt settlement
    </Link>{' '}
    and{' '}
    <Link
      href="/debtconsolidation/"
      className={TOOLTIP_LINK_CLASS}
      tabIndex={-1}
    >
      debt consolidation
    </Link>
    , reduces{' '}
    <Link
      href="/credit-card-debt/"
      className={TOOLTIP_LINK_CLASS}
      tabIndex={-1}
    >
      credit card debt
    </Link>{' '}
    and other unsecured debts.
  </p>
)

const renderTitleWithTooltip = (
  tooltipId,
  placement = 'bottom',
  isDefaultLang = true
) => (
  <>
    <Tooltip
      id={tooltipId}
      body={TOOLTIP_BODY}
      triggerClassName={DASHED_UNDERLINE}
      placement={placement}
    >
      DEBT RELIEF
    </Tooltip>
    {isDefaultLang ? ' THAT WORKS' : ' QUE FUNCIONA'}
  </>
)

const HeroBannerContent = ({
  content,
  isDefaultLang = true,
  isMobileVariantBActive,
  extraTitleClass = '',
  sliderValueColorClassName = 'text-content-information',
}) => {
  const {
    title,
    sliderTitle,
    buttonText,
    onSliderChange = () => {},
    defaultDebtAmount,
    getRid = 'Get rid of debt',
  } = content
  const { APPLY_FDR_URL } = getConfig()?.publicRuntimeConfig
  const { query: baseQuery } = useRouter()
  const { track, utm_source } = useAnalytics()
  const cookieLeadId = useLeadId()
  const cookies = getCookies()
  const APPLY_URL = isDefaultLang
    ? `${APPLY_FDR_URL}/home/`
    : `${APPLY_FDR_URL}/espanol/`
  const [estimatedDebt, setDebt] = useState(defaultDebtAmount || 25000)
  const { navigateToApply } = useNavigateToApply({
    utm_source,
    estimated_debt: estimatedDebt,
  })

  let query = useMemo(() => {
    const urlParams = {
      from: 'fdr',
      utm_source,
      ...baseQuery,
      estimated_debt: estimatedDebt,
      leadId: cookieLeadId,
      visit_id: cookies?.eh_visit_id || null,
    }

    for (let key in urlParams) {
      if (!urlParams[key]) {
        delete urlParams[key]
      }
    }

    const q = new URLSearchParams(urlParams)

    return q.toString()
  }, [baseQuery, cookieLeadId, cookies?.eh_visit_id, estimatedDebt, utm_source])

  const handleSliderChange = useCallback((event) => {
    const { value } = event.target
    setDebt(value)
    onSliderChange(value)
  }, [])

  const handleSliderClick = useCallback(
    (e) => {
      e.preventDefault()

      const data = {
        nav_link_section: 'Page',
        page_section: sliderTitle,
        click_type: 'Button Click',
        click_id: buttonText,
        click_text: `FDR-Web | ${buttonText}`,
        form_ss_amount: +estimatedDebt,
        track_event: 'button_click',
      }

      if (typeof track === 'function') {
        track(data, {}, 'button_click')
      }

      navigateToApply()
    },
    [sliderTitle, buttonText, estimatedDebt, track, navigateToApply]
  )

  const handleSliderRelease = useCallback(
    (e) => {
      const data = {
        nav_link_section: 'Page',
        page_section: sliderTitle,
        click_type: 'Button Click',
        click_id: 'slider release',
        click_text: `$${e.target.value}`,
        form_ss_amount: +e.target.value,
        track_event: 'button_click',
      }
      if (typeof track === 'function') {
        track(data, {}, 'button_click')
      }
    },
    [track, sliderTitle]
  )

  const handlePreviewClick = () => {
    track(
      {
        event_type: 'track',
        click_id: 'Trustpilot',
        click_type: 'Link Click',
        nav_link_section: 'Hero Card',
        additional_info: {
          section: 'trust-marker-truspilot',
          click_url: content?.trustPilot?.link || '',
        },
      },
      {},
      'button_click'
    )
  }

  const varinatBClassName = isMobileVariantBActive ? 'hidden sm:block' : ''
  const subTitle = isMobileVariantBActive ? getRid : title

  return (
    <div className="" id="hero-content-v10" data-testid="home-hero-content">
      <div className="max-w-full" data-testid="home-hero-title-section">
        <Section.Typography
          as="h1"
          variant="label-overline"
          className="sr-only"
          data-testid="home-hero-title"
        >
          {isDefaultLang
            ? 'DEBT RELIEF THAT WORKS'
            : 'DEBT RELIEF QUE FUNCIONA'}
        </Section.Typography>
        <div className="hidden text-center sm:block">
          <Section.Typography as="span" variant="label-overline">
            {renderTitleWithTooltip(
              'hero-main-title-tooltip-desktop',
              'bottom',
              isDefaultLang
            )}
          </Section.Typography>
          <Section.Typography
            as="h2"
            variant="display-large"
            className="mt-4 block"
            data-testid="home-hero-subtitle"
          >
            {subTitle}
          </Section.Typography>
        </div>

        <div
          className={`m-full px-5.5 sm:max-w-148 mx-auto -mt-9 mb-3 flex max-w-full flex-col items-center rounded-8 bg-white py-6 text-center shadow-2xl sm:mt-3 sm:w-full sm:px-0 lg:my-4`}
          data-testid="home-hero-slider-wrapper"
        >
          {isMobileVariantBActive && (
            <div className="flex w-full flex-col items-center sm:hidden">
              <Section.Typography
                as="span"
                variant="label-overline"
                className="text-center text-14 font-bold uppercase leading-[120%] tracking-[3.5px] text-content-secondary"
              >
                {renderTitleWithTooltip(
                  'hero-main-title-tooltip-mobile',
                  'top',
                  isDefaultLang
                )}
              </Section.Typography>
              <Section.Typography
                as="h2"
                variant="display-large"
                className="pt-[10px] pb-4"
              >
                {subTitle}
              </Section.Typography>
              <div
                className={`mb-4 flex w-full max-w-[303px] flex-col items-center justify-center rounded-8 bg-secondary py-4`}
              >
                <h2
                  className={`mb-4 text-center text-body-medium font-medium leading-[120%] text-content-primary`}
                >
                  {sliderTitle}
                </h2>
                <Slider
                  valueClassName={`w-full text-center mb-2 text-[20px] font-normal leading-[120%] tracking-[-0.2px] text-content-primary`}
                  containerClassName={classNames('w-4/5 pb-5', styles.slider)}
                  sliderClassName={
                    'mb-3 w-full appearance-none rounded-lg bg-gray-55 bg-slider-track-2 bg-no-repeat outline-none h-[4px] w-full'
                  }
                  showMinMaxLabels
                  onChange={handleSliderChange}
                  start={+estimatedDebt}
                  name="estimateSlider"
                  step={1000}
                  min={1000}
                  max={100000}
                  unit="$"
                  onSliderRelease={handleSliderRelease}
                  minMaxLabelsClassName={`text-[16px] font-medium leading-[120%] text-content-secondary`}
                />
              </div>
            </div>
          )}
          <Section.Typography
            as="h2"
            variant="display-small"
            className="hidden sm:block"
          >
            {sliderTitle}
          </Section.Typography>
          <Slider
            valueClassName={`${sliderValueColorClassName} text-[28px] w-full text-center mb-[0.2rem]`}
            containerClassName={classNames(
              'mb-10 w-11/12',
              styles.slider,
              `${varinatBClassName}`
            )}
            sliderClassName={
              'mb-3 w-full appearance-none rounded-lg bg-gray-55 bg-slider-track-2 bg-no-repeat outline-none h-[4px] w-full'
            }
            showMinMaxLabels
            onChange={handleSliderChange}
            start={+estimatedDebt}
            name="estimateSlider"
            step={1000}
            min={1000}
            max={100000}
            unit="$"
            onSliderRelease={handleSliderRelease}
          />
          <AnimatedArrowRedCTA
            id="hero-banner-content-cta"
            buttonText={buttonText}
            url={`${APPLY_URL}?${query}`}
            onClick={handleSliderClick}
            className="w-full max-w-[303px] lg:max-w-[327px]"
            data-testid="home-hero-cta-button"
          />
          {content.belowButtonText && (
            <p
              className="mt-3 text-12 leading-[140%] text-neutral-300 lg:text-14"
              data-testid="home-hero-cta-below-text"
            >
              {content.belowButtonText}
            </p>
          )}
          {content.newTrustpilotItem && (
            <div
              className={`${content.belowButtonText ? '-mt-primary-xxs' : ''}`}
              data-testid="home-hero-trustpilot-wrapper"
            >
              <TrustPilot
                {...content.trustPilot}
                loading="eager"
                onReviewClick={handlePreviewClick}
                footerTextClassName={`${
                  varinatBClassName
                    ? 'text-center text-[14px] font-normal leading-[140%] text-content-primary'
                    : ''
                }`}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

HeroBannerContent.propTypes = {
  content: PropTypes.object.isRequired,
  isDefaultLang: PropTypes.bool,
  shortView: PropTypes.bool,
  sliderValueColorClassName: PropTypes.string,
}
export default HeroBannerContent
